<script setup>
// Auditoria → Custos: o plano da empresa no ciclo. As mensagens pela API
// oficial contra a franquia, os relatórios da IA contra a cota, a fatura até
// agora e a previsão no fechamento, o upgrade sugerido quando outro plano sai
// mais barato, o gráfico por dia e os ciclos fechados.
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { createGestaoApi, reais } from './api';
import messages from './i18n';

const props = defineProps({
  accountId: { type: [Number, String], required: true },
});

const { t } = useI18n({ messages });
const api = computed(() => createGestaoApi(props.accountId));

const dados = ref(null);
const carregando = ref(true);
const erro = ref(false);

const carrega = async () => {
  carregando.value = true;
  erro.value = false;
  try {
    const { data } = await api.value.custos();
    dados.value = data;
  } catch {
    erro.value = true;
  } finally {
    carregando.value = false;
  }
};
onMounted(carrega);

const mil = n => Number(n || 0).toLocaleString('pt-BR');
const data = dia => (dia ? dia.split('-').reverse().join('/') : '—');
const centavos = valor =>
  Number(valor || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 4,
  });

const mensagens = computed(() => dados.value?.mensagens || {});
const barra = computed(() => Math.min(mensagens.value.uso || 0, 100));
const corDaBarra = computed(() => {
  if (mensagens.value.aviso === 100) return 'bg-n-ruby-9';
  if (mensagens.value.aviso === 80) return 'bg-n-amber-9';
  return 'bg-n-blue-9';
});

// Mensagens por dia, com a linha do ritmo da franquia (inclusas ÷ dias do ciclo).
const LARGURA = 720;
const ALTURA = 180;
const MARGEM = { esquerda: 48, base: 22, topo: 8 };
const ritmo = computed(() => {
  const inclusas = mensagens.value.inclusas;
  const { inicio, fecha_em: fecha } = dados.value?.ciclo || {};
  if (!inclusas || !inicio) return null;
  const dias = Math.round((new Date(fecha) - new Date(inicio)) / 86400000);
  return Math.round(inclusas / Math.max(dias, 1));
});
const grafico = computed(() => {
  const pontos = mensagens.value.por_dia || [];
  const maximo = Math.max(...pontos.map(ponto => ponto.n), ritmo.value || 0, 1);
  const util = ALTURA - MARGEM.base - MARGEM.topo;
  const largura = (LARGURA - MARGEM.esquerda) / Math.max(pontos.length, 1);
  const y = valor => ALTURA - MARGEM.base - (valor / maximo) * util;
  return {
    barras: pontos.map((ponto, i) => ({
      x: MARGEM.esquerda + i * largura + 2,
      y: y(ponto.n),
      largura: Math.max(largura - 4, 1),
      altura: ALTURA - MARGEM.base - y(ponto.n),
      titulo: `${data(ponto.dia)}: ${mil(ponto.n)}`,
      rotulo: i === 0 || i === pontos.length - 1 ? data(ponto.dia).slice(0, 5) : '',
    })),
    linhas: [0, 0.5, 1].map(fracao => ({ y: y(maximo * fracao), valor: mil(Math.round(maximo * fracao)) })),
    ritmo: ritmo.value ? y(ritmo.value) : null,
  };
});
</script>

<template>
  <div class="flex flex-col gap-5">
    <div v-if="carregando && !dados" class="flex justify-center py-10"><Spinner /></div>
    <div v-else-if="erro && !dados" class="flex items-center gap-3 text-sm text-n-ruby-11">
      {{ t('NEUXS_GESTAO.ERRO') }}
      <Button size="sm" variant="faded" :label="t('NEUXS_GESTAO.TENTAR')" @click="carrega" />
    </div>
    <p v-else-if="dados?.sem_plano" class="p-4 m-0 text-sm rounded-xl bg-n-alpha-2 text-n-slate-11">
      {{ t('NEUXS_GESTAO.CUSTOS.SEM_PLANO') }}
    </p>

    <template v-else-if="dados">
      <div class="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h2 class="m-0 text-heading-2 text-n-slate-12">{{ t('NEUXS_GESTAO.CUSTOS.PLANO', { nome: dados.plano.nome }) }}</h2>
        <span class="text-sm text-n-slate-11">
          {{ t('NEUXS_GESTAO.CUSTOS.CICLO', { de: data(dados.ciclo.inicio), ate: data(dados.ciclo.fim) }) }} ·
          {{
            dados.ciclo.fechado
              ? t('NEUXS_GESTAO.CUSTOS.FECHADO')
              : t('NEUXS_GESTAO.CUSTOS.FALTAM', { fecha: data(dados.ciclo.fecha_em), n: dados.ciclo.dias_restantes })
          }}
        </span>
      </div>

      <p v-if="mensagens.aviso === 100" class="p-3 m-0 text-sm rounded-xl bg-n-ruby-3 text-n-ruby-11">
        {{ t('NEUXS_GESTAO.CUSTOS.AVISO_100', { preco: centavos(dados.plano.preco_mensagem) }) }}
      </p>
      <p v-else-if="mensagens.aviso === 80" class="p-3 m-0 text-sm rounded-xl bg-n-amber-3 text-n-amber-11">
        {{ t('NEUXS_GESTAO.CUSTOS.AVISO_80', { uso: Math.floor(mensagens.uso) }) }}
      </p>
      <p v-if="dados.sugestao" class="p-3 m-0 text-sm rounded-xl bg-n-teal-3 text-n-teal-11">
        {{
          t('NEUXS_GESTAO.CUSTOS.SUGESTAO', {
            plano: dados.sugestao.plano,
            valor: reais(dados.sugestao.previsao),
            economia: reais(dados.sugestao.economia),
          })
        }}
      </p>

      <div class="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div class="flex flex-col gap-2 px-4 py-3 border rounded-xl border-n-weak">
          <span class="text-xs text-n-slate-11" :title="t('NEUXS_GESTAO.CUSTOS.MENSAGENS_DICA')">
            {{ t('NEUXS_GESTAO.CUSTOS.MENSAGENS') }}
          </span>
          <div class="flex items-baseline gap-2">
            <strong class="text-heading-1 text-n-slate-12 tabular-nums">{{ mil(mensagens.total) }}</strong>
            <span class="text-sm text-n-slate-11">
              {{
                mensagens.inclusas === null
                  ? t('NEUXS_GESTAO.CUSTOS.ILIMITADAS')
                  : t('NEUXS_GESTAO.CUSTOS.INCLUSAS', { n: mil(mensagens.inclusas) })
              }}
            </span>
          </div>
          <div v-if="mensagens.inclusas !== null" class="h-2 overflow-hidden rounded-full bg-n-alpha-2">
            <div class="h-full rounded-full" :class="corDaBarra" :style="{ width: `${barra}%` }" />
          </div>
          <span class="text-xs text-n-slate-11">
            {{ t('NEUXS_GESTAO.CUSTOS.DETALHE', { respostas: mil(mensagens.respostas), modelos: mil(mensagens.modelos) }) }}
          </span>
          <span v-if="!dados.ciclo.fechado" class="text-xs text-n-slate-11">
            {{ t('NEUXS_GESTAO.CUSTOS.PROJECAO', { n: mil(mensagens.projecao) }) }}
          </span>
        </div>

        <div class="flex flex-col gap-2 px-4 py-3 border rounded-xl border-n-weak">
          <span class="text-xs text-n-slate-11">{{ t('NEUXS_GESTAO.CUSTOS.RELATORIOS') }}</span>
          <strong v-if="!dados.plano.auditoria" class="text-heading-2 font-normal text-n-slate-11">
            {{ t('NEUXS_GESTAO.CUSTOS.SEM_AUDITORIA') }}
          </strong>
          <template v-else>
            <strong class="text-heading-1 text-n-slate-12 tabular-nums">
              {{ t('NEUXS_GESTAO.CUSTOS.RELATORIOS_USO', { usados: dados.relatorios.usados, inclusos: dados.relatorios.inclusos }) }}
            </strong>
            <span v-if="dados.relatorios.extras" class="text-xs text-n-slate-11">
              {{ t('NEUXS_GESTAO.CUSTOS.RELATORIOS_EXTRAS', { n: dados.relatorios.extras, valor: reais(dados.relatorios.valor_extras) }) }}
            </span>
          </template>
        </div>
      </div>

      <section class="flex flex-col gap-2 px-4 py-3 border rounded-xl border-n-weak">
        <h2 class="m-0 text-heading-3 text-n-slate-12">{{ t('NEUXS_GESTAO.CUSTOS.FATURA') }}</h2>
        <table class="w-full text-sm">
          <tbody class="divide-y divide-n-weak">
            <tr>
              <td class="py-2 text-n-slate-11">{{ t('NEUXS_GESTAO.CUSTOS.MENSALIDADE') }}</td>
              <td class="py-2 text-end tabular-nums">{{ reais(dados.valores.mensalidade) }}</td>
            </tr>
            <tr>
              <td class="py-2 text-n-slate-11">
                {{ t('NEUXS_GESTAO.CUSTOS.EXCEDENTE') }}
                <span v-if="mensagens.excedentes" class="text-xs text-n-slate-10">
                  · {{ t('NEUXS_GESTAO.CUSTOS.EXCEDENTES', { n: mil(mensagens.excedentes), preco: centavos(dados.plano.preco_mensagem) }) }}
                </span>
              </td>
              <td class="py-2 text-end tabular-nums">{{ reais(dados.valores.excedente) }}</td>
            </tr>
            <tr v-if="dados.plano.auditoria || dados.valores.relatorios">
              <td class="py-2 text-n-slate-11">{{ t('NEUXS_GESTAO.CUSTOS.EXTRAS') }}</td>
              <td class="py-2 text-end tabular-nums">{{ reais(dados.valores.relatorios) }}</td>
            </tr>
            <tr>
              <td class="py-2 font-medium text-n-slate-12">{{ t('NEUXS_GESTAO.CUSTOS.ATE_AGORA') }}</td>
              <td class="py-2 font-medium text-end tabular-nums text-n-slate-12">{{ reais(dados.valores.total) }}</td>
            </tr>
            <tr v-if="!dados.ciclo.fechado">
              <td class="py-2 text-n-slate-11">{{ t('NEUXS_GESTAO.CUSTOS.PREVISAO') }}</td>
              <td class="py-2 text-end tabular-nums text-n-slate-11">{{ reais(dados.valores.previsao) }}</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section class="flex flex-col gap-2">
        <div class="flex flex-wrap items-baseline gap-3">
          <h2 class="m-0 text-heading-3 text-n-slate-12">{{ t('NEUXS_GESTAO.CUSTOS.POR_DIA') }}</h2>
          <span v-if="ritmo" class="text-xs text-n-amber-11">— {{ t('NEUXS_GESTAO.CUSTOS.RITMO', { n: mil(ritmo) }) }}</span>
        </div>
        <svg :viewBox="`0 0 ${LARGURA} ${ALTURA}`" class="w-full h-auto" role="img">
          <g v-for="(linha, i) in grafico.linhas" :key="`l${i}`">
            <line :x1="MARGEM.esquerda" :x2="LARGURA" :y1="linha.y" :y2="linha.y" class="stroke-n-weak" stroke-width="1" />
            <text :x="MARGEM.esquerda - 6" :y="linha.y + 4" text-anchor="end" class="fill-n-slate-10 text-[10px]">
              {{ linha.valor }}
            </text>
          </g>
          <g v-for="(barra, i) in grafico.barras" :key="`b${i}`">
            <rect :x="barra.x" :y="barra.y" :width="barra.largura" :height="barra.altura" rx="2" class="fill-n-blue-9">
              <title>{{ barra.titulo }}</title>
            </rect>
            <text
              v-if="barra.rotulo"
              :x="barra.x + barra.largura / 2"
              :y="ALTURA - 6"
              text-anchor="middle"
              class="fill-n-slate-10 text-[10px]"
            >
              {{ barra.rotulo }}
            </text>
          </g>
          <line
            v-if="grafico.ritmo !== null"
            :x1="MARGEM.esquerda"
            :x2="LARGURA"
            :y1="grafico.ritmo"
            :y2="grafico.ritmo"
            class="stroke-n-amber-9"
            stroke-width="1.5"
            stroke-dasharray="6 4"
          />
        </svg>
      </section>

      <section v-if="dados.ciclos.length" class="flex flex-col gap-2">
        <h2 class="m-0 text-heading-3 text-n-slate-12">{{ t('NEUXS_GESTAO.CUSTOS.CICLOS') }}</h2>
        <table class="w-full text-sm">
          <thead>
            <tr class="text-xs text-left text-n-slate-11">
              <th class="py-2 pe-3 font-normal">{{ t('NEUXS_GESTAO.CUSTOS.PERIODO') }}</th>
              <th class="py-2 pe-3 font-normal">{{ t('NEUXS_GESTAO.CUSTOS.PLANO_COLUNA') }}</th>
              <th class="py-2 pe-3 font-normal text-end">{{ t('NEUXS_GESTAO.CUSTOS.MENSAGENS_COLUNA') }}</th>
              <th class="py-2 font-normal text-end">{{ t('NEUXS_GESTAO.CUSTOS.TOTAL') }}</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-n-weak">
            <tr v-for="ciclo in dados.ciclos" :key="ciclo.inicio">
              <td class="py-2 pe-3 text-n-slate-12">{{ data(ciclo.inicio) }} – {{ data(ciclo.fim) }}</td>
              <td class="py-2 pe-3 text-n-slate-11">{{ ciclo.plano }}</td>
              <td class="py-2 pe-3 text-end tabular-nums text-n-slate-11">{{ mil(ciclo.mensagens) }}</td>
              <td class="py-2 text-end tabular-nums text-n-slate-12">{{ reais(ciclo.total) }}</td>
            </tr>
          </tbody>
        </table>
      </section>
    </template>
  </div>
</template>
